import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  FormControlLabel,
  Radio,
  Switch,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Grid,
  Tooltip,
} from '@mui/material';
import { ExpandMore, Add, Close, Check, Warning, Edit } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { Locale } from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import Navigation from '../Shared/Navigation.tsx';
import { useTranslation } from '@/core/i18n/i18n';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import { useNavigate } from 'react-router-dom';
import { useAnyLoading } from '@/core/useAnyLoading';
import {
  Activity,
  ActivityBatchSaveInput,
  Focus,
  FocusRequirementWithDetails,
  Objective,
  ActivityTypes,
  PlanRequirementDecision,
} from '@shared/schema';
import {
  buildPlanRequirementDecisionLookup,
  filterRequirementsByDecisions,
} from '@/core/helpers/planRequirements';

const MAX_TEXT_LENGTH = 255;

const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  'en-us': enUS,
  es,
  'es-es': es,
  pt: ptBR,
  'pt-br': ptBR,
  br: ptBR,
};

const formatPercentage = (value: number | null | undefined, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Number(num.toFixed(digits));
  const safe = rounded === 0 ? 0 : rounded;
  const str = safe.toFixed(digits);
  return str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

interface DefiniractivitiesProps {
  objectives: Objective[];
  activities: Activity[];
  requirements: FocusRequirementWithDetails[];
  planRequirementDecisions: PlanRequirementDecision[];
  focuses: Focus[];
  currentVersion: number | null;
  currentPlanId: number | null;
  readOnlyMode: boolean | null;
  selectedYear: string | number | null;
  currentBusinessUnit?: string | null;
  fetchAnnualPlanStep2Context: (
    versionId: number,
    params?: { planId?: number; year?: number },
  ) => Promise<unknown>;
  saveActivitiesBatch: (versionId: number, data: ActivityBatchSaveInput) => Promise<unknown>;
  markPlanVersionForApproval: (versionId: number) => Promise<void>;
  scopeName: string;
  enterpriseName: string;
}

const Definiractivities: React.FC<DefiniractivitiesProps> = ({
  objectives,
  activities,
  requirements,
  planRequirementDecisions,
  focuses,
  selectedYear,
  currentBusinessUnit,
  fetchAnnualPlanStep2Context,
  saveActivitiesBatch,
  markPlanVersionForApproval,
  currentVersion,
  currentPlanId,
  readOnlyMode,
  scopeName,
  enterpriseName,
}) => {
  const isAnyLoading = useAnyLoading();
  const { t } = useTranslation();
  const [expandedFoco, setExpandedFoco] = useState<number | false>(1);
  const [objectivesByFocus, setObjectivesByFocus] = useState<{
    [key: number]: Objective[];
  }>({});
  const [expandedObjetivo, setExpandedObjetivo] = useState<number>(1);
  const [activitiesPorObjetivo, setactivitiesPorObjetivo] = useState<{
    [key: string]: Activity[];
  }>({});
  const [activityEnEdicion, setactivityEnEdicion] = useState<{
    [key: string]: Activity;
  }>({});
  const [editandoactivity, setEditandoactivity] = useState<{
    [key: string]: string | null;
  }>({});
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [existingActivityIds, setExistingActivityIds] = useState<Set<number>>(new Set());
  const [editedActivityIds, setEditedActivityIds] = useState<Set<number>>(new Set());
  const [deletedActivityIds, setDeletedActivityIds] = useState<Set<number>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<Record<number, { name?: string }>>({});
  const adapterLocale = React.useMemo<Locale>(() => {
    if (typeof navigator === 'undefined') {
      return enUS;
    }
    const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
    const normalized = browserLanguage.toLowerCase();
    const base = normalized.split('-')[0] || 'en';
    return DATE_FNS_LOCALES[normalized] || DATE_FNS_LOCALES[base] || enUS;
  }, []);
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);
  const yearBoundaries = React.useMemo(
    () => ({
      start: new Date(currentYear, 0, 1),
      end: new Date(currentYear, 11, 31, 23, 59, 59, 999),
    }),
    [currentYear],
  );
  const decisionsForCurrentPlan = React.useMemo(
    () =>
      (planRequirementDecisions ?? []).filter((decision) =>
        currentPlanId ? decision.APId === currentPlanId : true,
      ),
    [planRequirementDecisions, currentPlanId],
  );
  const decisionLookup = React.useMemo(
    () => buildPlanRequirementDecisionLookup(decisionsForCurrentPlan),
    [decisionsForCurrentPlan],
  );
  const visibleRequirements = React.useMemo(() => {
    if (!requirements || requirements.length === 0) {
      return [] as FocusRequirementWithDetails[];
    }
    if (!currentPlanId || decisionsForCurrentPlan.length === 0) {
      return requirements;
    }
    return filterRequirementsByDecisions(requirements, decisionLookup);
  }, [requirements, decisionsForCurrentPlan, decisionLookup, currentPlanId]);
  const setFieldError = React.useCallback((objetivoId: number, field: 'name', message?: string) => {
    setFieldErrors((prev) => {
      const updated = { ...prev };
      const current = { ...(updated[objetivoId] || {}) };
      if (message) {
        current[field] = message;
      } else {
        delete current[field];
      }
      if (Object.keys(current).length === 0) {
        delete updated[objetivoId];
      } else {
        updated[objetivoId] = current;
      }
      return updated;
    });
  }, []);
  const adherenceByFocus = React.useMemo(() => {
    if (!focuses || focuses.length === 0)
      return {} as Record<
        number,
        { TotalRequired: number; SatisfiedRequired: number; AdherencePct: number }
      >;
    const requiredReqsPerFocus: Record<number, Set<number>> = {};
    visibleRequirements.forEach((fr) => {
      fr.RequirementTypes.forEach((rt) => {
        if (rt.requirementType.isRequired) {
          rt.requirements.forEach((r) => {
            if (!requiredReqsPerFocus[fr.APFocusId]) requiredReqsPerFocus[fr.APFocusId] = new Set();
            requiredReqsPerFocus[fr.APFocusId]!.add(r.APRequirementId);
          });
        }
      });
    });

    const objectivesByFocusLocal: Record<number, number[]> = {};
    objectives.forEach((o) => {
      if (!objectivesByFocusLocal[o.APFocusId]) objectivesByFocusLocal[o.APFocusId] = [];
      objectivesByFocusLocal[o.APFocusId]!.push(o.APObjectiveId);
    });

    const result: Record<
      number,
      { TotalRequired: number; SatisfiedRequired: number; AdherencePct: number }
    > = {};
    focuses.forEach((f) => {
      const requiredSet = requiredReqsPerFocus[f.APFocusId] || new Set();
      const totalRequired = requiredSet.size;
      const satisfied = new Set<number>();
      const objIds = objectivesByFocusLocal[f.APFocusId] || [];
      objIds.forEach((objId) => {
        const acts = activitiesPorObjetivo[objId] || [];
        acts.forEach((a) => {
          if (a.ApplyRequirement && a.APRequirementId && requiredSet.has(a.APRequirementId)) {
            satisfied.add(a.APRequirementId);
          }
        });
      });
      const satisfiedRequired = satisfied.size;
      const adherencePct = totalRequired > 0 ? (satisfiedRequired * 100) / totalRequired : 0;
      result[f.APFocusId] = {
        TotalRequired: totalRequired,
        SatisfiedRequired: satisfiedRequired,
        AdherencePct: adherencePct,
      };
    });
    return result;
  }, [focuses, visibleRequirements, objectives, activitiesPorObjetivo]);

  const globalAdherence = React.useMemo(() => {
    const weightsTotal = focuses.reduce((acc, f) => acc + (f.Weight || 0), 0);
    if (weightsTotal === 0) return 0;
    let weighted = 0;
    focuses.forEach((f) => {
      const adh = adherenceByFocus[f.APFocusId]?.AdherencePct ?? 0;
      const w = f.Weight || 0;
      weighted += adh * w;
    });
    return weighted / weightsTotal;
  }, [focuses, adherenceByFocus]);

  useEffect(() => {
    if (currentVersion) {
      const parsedYear = Number.parseInt(String(selectedYear ?? ''), 10);
      const year = Number.isInteger(parsedYear) ? parsedYear : undefined;
      fetchAnnualPlanStep2Context(currentVersion, {
        ...(currentPlanId ? { planId: currentPlanId } : {}),
        ...(year ? { year } : {}),
      });
    }
  }, [currentVersion, fetchAnnualPlanStep2Context, currentPlanId, selectedYear]);

  useEffect(() => {
    if (activities && activities.length > 0) {
      const activitiesAgrupadas: { [key: string]: Activity[] } = {};

      activities.forEach((activity) => {
        const objetivoId = activity.APObjectiveId.toString();
        if (!activitiesAgrupadas[objetivoId]) {
          activitiesAgrupadas[objetivoId] = [];
        }
        activitiesAgrupadas[objetivoId].push({
          ...activity,
        });
        if (activity.APGroupActivityId) {
          setExistingActivityIds((prev) => new Set(prev).add(activity.APGroupActivityId));
        }
      });

      setactivitiesPorObjetivo((prev) => {
        const nuevo = { ...prev };
        Object.entries(activitiesAgrupadas).forEach(([objetivoId, lista]) => {
          nuevo[objetivoId] = [
            ...(nuevo[objetivoId] || []),
            ...lista.filter(
              (aBackend) =>
                !(nuevo[objetivoId] || []).some(
                  (aLocal) => aLocal.APGroupActivityId === aBackend.APGroupActivityId,
                ),
            ),
          ];
        });
        return nuevo;
      });
    }
  }, [activities]);

  useEffect(() => {
    if (objectives && objectives.length > 0) {
      const objetivosAgrupados: { [key: number]: Objective[] } = {};
      objectives.forEach((objective) => {
        if (!objetivosAgrupados[objective.APFocusId]) {
          objetivosAgrupados[objective.APFocusId] = [];
        }
        (objetivosAgrupados[objective.APFocusId] ?? []).push(objective);
      });
      setObjectivesByFocus(objetivosAgrupados);
    }
  }, [objectives]);

  const handleFocoChange =
    (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFoco(isExpanded ? panel : false);
    };

  const handleObjetivoChange =
    (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedObjetivo(isExpanded ? panel : 0);
    };

  const handleactivityChange = (objetivoId: number, field: keyof Activity, value: unknown) => {
    setactivityEnEdicion((prev) => ({
      ...prev,
      [objetivoId]: {
        ...prev[objetivoId],
        [field]: value,
      },
    }));
  };

  const handleAgregaractivity = (objetivoId: number) => {
    const activity = activityEnEdicion[objetivoId];
    if (fieldErrors[objetivoId]?.name) return;
    if (activity && activity.GroupActivityName) {
      if (!activity.GroupActivityType) {
        activity.GroupActivityType = ActivityTypes.STRATEGIC;
      }

      if (!activity.ApplyRequirement) {
        activity.ApplyRequirement = false;
      }

      if (editandoactivity[objetivoId]) {
        handleGuardarEdicion(objetivoId);
      } else {
        const activityConId = {
          ...activity,
          APGroupActivityId: Date.now().toString(),
        };

        setactivitiesPorObjetivo((prev) => ({
          ...prev,
          [objetivoId]: [...(prev[objetivoId] || []), activityConId],
        }));

        setactivityEnEdicion((prev) => ({ ...prev, [objetivoId]: null }));
        setFieldError(objetivoId, 'name');
      }
    }
  };

  const handleEliminarActivity = (APObjectiveId: number, annualPlanGroupActivityId: number) => {
    if (existingActivityIds.has(Number(annualPlanGroupActivityId))) {
      setDeletedActivityIds((prev) => new Set(prev).add(Number(annualPlanGroupActivityId)));
    }
    setactivitiesPorObjetivo((prev) => {
      const current = prev[APObjectiveId] ?? [];
      return {
        ...prev,
        [APObjectiveId]: current.filter((a) => a.APGroupActivityId !== annualPlanGroupActivityId),
      };
    });
  };

  const handleEditarActivity = (APObjectiveId: number, activity: Activity) => {
    setEditandoactivity((prev) => ({ ...prev, [APObjectiveId]: activity }));
    setactivityEnEdicion((prev) => ({
      ...prev,
      [APObjectiveId]: { ...activity },
    }));
  };

  const handleGuardarEdicion = (objetivoId: number) => {
    if (fieldErrors[objetivoId]?.name) return;
    const activityEditada = activityEnEdicion[objetivoId];
    if (activityEditada) {
      setactivitiesPorObjetivo((prev) => {
        const currentActivities = prev[objetivoId] ?? [];
        return {
          ...prev,
          [objetivoId]: currentActivities.map((a) =>
            a.APGroupActivityId === activityEditada.APGroupActivityId ? activityEditada : a,
          ),
        };
      });
      if (
        typeof activityEditada.APGroupActivityId === 'number' ||
        /^[0-9]+$/.test(String(activityEditada.APGroupActivityId))
      ) {
        const numericId = Number(activityEditada.APGroupActivityId);
        if (existingActivityIds.has(numericId)) {
          setEditedActivityIds((prev) => new Set(prev).add(numericId));
        }
      }
      setEditandoactivity((prev) => ({ ...prev, [objetivoId]: null }));

      setactivityEnEdicion((prev) => ({ ...prev, [objetivoId]: null }));
      setFieldError(objetivoId, 'name');
    }
  };

  const getRequirementsForCurrentFocus = (focusId: number) => {
    if (!visibleRequirements || visibleRequirements.length === 0) return [];

    const focusRequirements = visibleRequirements.find((req) => req.APFocusId === focusId);
    if (!focusRequirements) return [];

    return [...focusRequirements.RequirementTypes].sort((a, b) => {
      const aRequired = a?.requirementType?.isRequired ? 0 : 1;
      const bRequired = b?.requirementType?.isRequired ? 0 : 1;
      if (aRequired !== bRequired) return aRequired - bRequired;
      return 0;
    });
  };

  const getRequirementNameById = (requirementId: number, focusId: number) => {
    if (!requirementId) return t('defineActivities.noApply');

    const currentFocusRequirements = getRequirementsForCurrentFocus(focusId);
    for (const reqType of currentFocusRequirements) {
      const req = reqType.requirements.find((r) => r.APRequirementId === requirementId);
      if (req) return req.Name;
    }
    return `${t('defineActivities.reqId')} ${requirementId}`;
  };

  const isFormValid = (objetivoId: number) => {
    const activity = activityEnEdicion[objetivoId];

    if (!activity) return false;
    if (fieldErrors[objetivoId]?.name) return false;

    const hasRequiredFields =
      activity.GroupActivityName &&
      activity.GroupActivityName.trim() !== '' &&
      activity.Description &&
      activity.Description.trim() !== '' &&
      activity.StartDate &&
      activity.EndDate;

    if (!hasRequiredFields) return false;

    if (activity.StartDate && activity.EndDate && activity.EndDate < activity.StartDate) {
      return false;
    }

    if (activity.ApplyRequirement) {
      return activity.APRequirementId && activity.APRequirementId > 0;
    }

    return true;
  };

  const getValidationMessage = (objetivoId: number) => {
    const activity = activityEnEdicion[objetivoId];

    if (!activity) return t('defineActivities.completeForm');
    if (fieldErrors[objetivoId]?.name) {
      return fieldErrors[objetivoId]?.name ?? t('defineActivities.completeForm');
    }

    const missingFields: string[] = [];

    if (!activity.GroupActivityName || activity.GroupActivityName.trim() === '') {
      missingFields.push(t('defineActivities.activityName'));
    }

    if (!activity.Description || activity.Description.trim() === '') {
      missingFields.push(t('defineActivities.description'));
    }

    if (!activity.StartDate) {
      missingFields.push(t('defineActivities.startDate'));
    }

    if (!activity.EndDate) {
      missingFields.push(t('defineActivities.endDate'));
    }

    if (activity.StartDate && activity.EndDate && activity.EndDate < activity.StartDate) {
      missingFields.push(t('defineActivities.endDateShouldBeAfterStartDate'));
    }

    if (activity.ApplyRequirement && (!activity.APRequirementId || activity.APRequirementId <= 0)) {
      missingFields.push(t('defineActivities.requirement'));
    }

    if (missingFields.length === 0) {
      return editandoactivity[objetivoId]
        ? t('defineActivities.saveChanges')
        : t('defineActivities.addActivity');
    }

    return `${t('defineActivities.missingFields')} ${missingFields.join(', ')}`;
  };

  const handleBack = () => {
    navigate('/define-objectives');
  };

  const handleExit = () => {
    navigate('/');
  };

  const handleContinue = () => {
    if (readOnlyMode) {
      navigate('/approval-definitions');
      return;
    }
    setShowConfirmModal(true);
  };

  const buildActivityBatchPayload = (): ActivityBatchSaveInput => {
    const create: ActivityBatchSaveInput['create'] = [];
    const update: ActivityBatchSaveInput['update'] = [];

    for (const [objetivoId, activities] of Object.entries(activitiesPorObjetivo)) {
      for (const activity of activities) {
        const activityId = Number(activity.APGroupActivityId);
        const payload = {
          APObjectiveId: activity.APObjectiveId || parseInt(objetivoId, 10),
          APRequirementId: activity.APRequirementId,
          GroupActivityName: activity.GroupActivityName,
          StartDate: activity.StartDate,
          EndDate: activity.EndDate,
          GroupActivityType: activity.GroupActivityType,
          Description: activity.Description,
          ApplyRequirement: activity.ApplyRequirement,
        };
        if (!existingActivityIds.has(activityId)) {
          create.push(payload);
        } else if (editedActivityIds.has(activityId)) {
          update.push({ ...payload, APGroupActivityId: activityId });
        }
      }
    }

    return {
      create,
      update,
      delete: Array.from(deletedActivityIds).filter((id) => id > 0),
    };
  };

  const persistActivities = async (submitForApproval: boolean) => {
    if (!currentVersion) return;
    try {
      await saveActivitiesBatch(currentVersion, buildActivityBatchPayload());
      setEditedActivityIds(new Set());
      setDeletedActivityIds(new Set());
      if (submitForApproval) {
        await markPlanVersionForApproval(currentVersion);
      }
      navigate('/');
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error saving activities:', error);
    }
  };

  const handleSaveProgress = async () => {
    await persistActivities(false);
  };

  const handleConfirmSave = async () => {
    await persistActivities(true);
  };

  const hasObjectivesWithoutActivities = objectives.some((objetivo) => {
    const activities = activitiesPorObjetivo[objetivo.APObjectiveId] || [];
    return activities.length === 0;
  });
  const disableNext = hasObjectivesWithoutActivities && !readOnlyMode;
  const disableNextReason = disableNext ? t('defineActivities.disableNextReason') : undefined;
  const navigationDisableProps = React.useMemo(
    () => (disableNextReason ? { disableReason: disableNextReason } : {}),
    [disableNextReason],
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: 4 }}>
      {/* Content */}
      <Box sx={{ p: 3, maxWidth: '1366px', mx: 'auto' }}>
        {/* Breadcrumb */}

        {/* Main Card */}
        <Paper sx={{ borderRadius: 1, boxShadow: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Grid
            container
            sx={{
              pt: 2,
              paddingLeft: 3,
              paddingRight: 3,

              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
            }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid size="auto">
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Bloque Plan Anual */}
                <Grid
                  size="auto"
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('common.annualPlan')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {selectedYear}
                  </Typography>
                </Grid>

                {/* Bloque Ámbito */}
                <Grid
                  size="auto"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeName}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('newAnnualPlan.annualPlanDefinition')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size="auto">
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={PlanAnualIcon}
                  alt={t('common.annualPlan')}
                  style={{ width: 70, height: 70 }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Stepper */}
          <Box sx={{ p: 3, bgcolor: 'white' }}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#D8FFEC',
                    color: '#333',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          bgcolor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto" sx={{ mt: 0.5 }}>
                          <Check />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step1')}
                      </Typography>
                      <Typography variant="caption">{t('common.step1Ttitle')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#00BCE4',
                    color: 'white',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          color: '#00BCE4',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">2</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step2')}
                      </Typography>
                      <Typography variant="caption">{t('common.step2Title')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#f5f5f5',
                    color: '#999',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          color: '#999',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">2</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step3')}
                      </Typography>
                      <Typography variant="caption">{t('common.step3Title')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            {/* Definir actividades Section */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              {t('defineActivities.defineActivities')}
            </Typography>

            {/* Focos Estratégicos */}
            <Box sx={{ p: 2, borderRadius: 1, border: '1px solid #A5B2BB' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                {t('defineActivities.strategicFocuses')}
              </Typography>

              {focuses.map((focus, index) => (
                <Accordion
                  key={focus.APFocusId}
                  expanded={expandedFoco === focus.APFocusId}
                  onChange={handleFocoChange(focus.APFocusId)}
                  sx={{
                    mb: 2,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: '#F4F8F8',
                      borderRadius: 1,
                      border: '1px solid #A5B2BB',
                      minHeight: '40px !important',
                      height: '40px !important',
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        margin: '8px 0 !important',
                      },
                      '&.Mui-expanded': {
                        minHeight: '40px !important',
                        height: '40px !important',
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '8px 0 !important',
                      },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {index + 1}. {focus.Name}
                    </Typography>
                  </AccordionSummary>
                  {expandedFoco === focus.APFocusId && (
                    <>
                      <AccordionDetails sx={{ bgcolor: 'white', pt: 3, mx: -1.75 }}>
                        {(objectivesByFocus[focus.APFocusId] ?? []).length > 0 ? (
                          <Box sx={{ p: 2, borderRadius: 1, border: '1px solid #A5B2BB' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('defineActivities.objectives')}
                            </Typography>

                            {objectivesByFocus[focus.APFocusId]?.map((objetivo, _index) => (
                              <Accordion
                                key={objetivo.APObjectiveId}
                                expanded={expandedObjetivo === objetivo.APObjectiveId}
                                onChange={handleObjetivoChange(objetivo.APObjectiveId)}
                                sx={{
                                  mb: 2,
                                  boxShadow: 'none',
                                  '&:before': { display: 'none' },
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMore />}
                                  sx={{
                                    bgcolor: '#e3f9fd',
                                    borderRadius: 1,
                                    border: '1px solid #A5B2BB',
                                    minHeight: '40px !important',
                                    '& .MuiAccordionSummary-content': {
                                      alignItems: 'center',
                                      margin: '12px 0 !important',
                                      py: 0.5,
                                    },
                                    '&.Mui-expanded': {
                                      minHeight: '40px !important',
                                    },
                                    '& .MuiAccordionSummary-content.Mui-expanded': {
                                      margin: '12px 0 !important',
                                    },
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ pr: 2, wordBreak: 'break-word', lineHeight: 1.5 }}
                                  >
                                    {objetivo.Name}
                                  </Typography>
                                </AccordionSummary>
                                {expandedObjetivo === objetivo.APObjectiveId && (
                                  <AccordionDetails sx={{ bgcolor: 'white', pt: 3, mx: -1.75 }}>
                                    <Box
                                      sx={{
                                        p: 3,
                                        borderRadius: 1,
                                        mb: 1,
                                        bgcolor: 'white',
                                        border: '1px solid #A5B2BB',
                                      }}
                                    >
                                      {!readOnlyMode && (
                                        <>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 2, fontWeight: 600 }}
                                          >
                                            {t('defineActivities.enterActivities')}
                                          </Typography>
                                          <Box
                                            sx={{
                                              p: 3,
                                              borderRadius: 1,
                                              mb: 1,
                                              bgcolor: 'white',
                                              border: '1px solid #A5B2BB',
                                            }}
                                          >
                                            <Grid container spacing={2}>
                                              <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography
                                                  variant="subtitle2"
                                                  sx={{ mb: 0.5, fontWeight: 600 }}
                                                >
                                                  {t('defineActivities.activityName')}
                                                </Typography>
                                                <Box>
                                                  <TextField
                                                    fullWidth
                                                    required
                                                    placeholder={t('defineActivities.enterText')}
                                                    value={
                                                      activityEnEdicion[objetivo.APObjectiveId]
                                                        ?.GroupActivityName || ''
                                                    }
                                                    onChange={(e) => {
                                                      const value = e.target.value;
                                                      if (value.length > MAX_TEXT_LENGTH) {
                                                        handleactivityChange(
                                                          objetivo.APObjectiveId,
                                                          'GroupActivityName',
                                                          value.slice(0, MAX_TEXT_LENGTH),
                                                        );
                                                        setFieldError(
                                                          objetivo.APObjectiveId,
                                                          'name',
                                                          t('defineActivities.maxLengthExceeded', {
                                                            max: MAX_TEXT_LENGTH,
                                                          }),
                                                        );
                                                        return;
                                                      }
                                                      setFieldError(objetivo.APObjectiveId, 'name');
                                                      handleactivityChange(
                                                        objetivo.APObjectiveId,
                                                        'GroupActivityName',
                                                        value,
                                                      );
                                                    }}
                                                    onPaste={(event) => {
                                                      const pastedText =
                                                        event.clipboardData?.getData('text') ?? '';
                                                      const currentValue =
                                                        activityEnEdicion[objetivo.APObjectiveId]
                                                          ?.GroupActivityName || '';
                                                      if (
                                                        currentValue.length + pastedText.length >
                                                        MAX_TEXT_LENGTH
                                                      ) {
                                                        event.preventDefault();
                                                        const allowed =
                                                          MAX_TEXT_LENGTH - currentValue.length;
                                                        const nextValue =
                                                          currentValue +
                                                          pastedText.slice(0, Math.max(allowed, 0));
                                                        handleactivityChange(
                                                          objetivo.APObjectiveId,
                                                          'GroupActivityName',
                                                          nextValue,
                                                        );
                                                        setFieldError(
                                                          objetivo.APObjectiveId,
                                                          'name',
                                                          t('defineActivities.maxLengthExceeded', {
                                                            max: MAX_TEXT_LENGTH,
                                                          }),
                                                        );
                                                      }
                                                    }}
                                                    size="small"
                                                    error={Boolean(
                                                      fieldErrors[objetivo.APObjectiveId]?.name,
                                                    )}
                                                    helperText={
                                                      fieldErrors[objetivo.APObjectiveId]?.name
                                                    }
                                                    inputProps={{ maxLength: MAX_TEXT_LENGTH }}
                                                    sx={{
                                                      mb: 0.5,
                                                      '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'white',
                                                        '& fieldset': {
                                                          borderColor: '#A5B2BB',
                                                        },
                                                      },
                                                    }}
                                                  />
                                                  <Box sx={{ mb: 2 }}>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        fontSize: '0.8rem',
                                                        fontWeight: 500,
                                                        color:
                                                          (
                                                            activityEnEdicion[
                                                              objetivo.APObjectiveId
                                                            ]?.GroupActivityName || ''
                                                          ).length >= MAX_TEXT_LENGTH
                                                            ? '#d32f2f'
                                                            : '#555',
                                                      }}
                                                    >
                                                      <strong>
                                                        {t('defineActivities.maxCharacters')}
                                                      </strong>{' '}
                                                      {t('defineActivities.remainingCharacters')}{' '}
                                                      <strong
                                                        style={{
                                                          color:
                                                            (
                                                              activityEnEdicion[
                                                                objetivo.APObjectiveId
                                                              ]?.GroupActivityName || ''
                                                            ).length >= MAX_TEXT_LENGTH
                                                              ? '#d32f2f'
                                                              : '#00BCE4',
                                                        }}
                                                      >
                                                        {Math.max(
                                                          0,
                                                          MAX_TEXT_LENGTH -
                                                            (
                                                              activityEnEdicion[
                                                                objetivo.APObjectiveId
                                                              ]?.GroupActivityName || ''
                                                            ).length,
                                                        )}
                                                      </strong>
                                                    </Typography>
                                                  </Box>
                                                </Box>

                                                <Typography
                                                  variant="subtitle2"
                                                  sx={{ mb: 0.5, fontWeight: 600 }}
                                                >
                                                  {t('defineActivities.activityType')}
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    border: '1px solid #A5B2BB',
                                                    borderRadius: 1,
                                                    bgcolor: 'white',
                                                    p: 3,
                                                    mb: 2,
                                                  }}
                                                >
                                                  <Grid container>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                      <FormControlLabel
                                                        value={ActivityTypes.STRATEGIC}
                                                        checked={
                                                          activityEnEdicion[objetivo.APObjectiveId]
                                                            ?.GroupActivityType ===
                                                            ActivityTypes.STRATEGIC ||
                                                          !activityEnEdicion[objetivo.APObjectiveId]
                                                            ?.GroupActivityType
                                                        }
                                                        onChange={() =>
                                                          handleactivityChange(
                                                            objetivo.APObjectiveId,
                                                            'GroupActivityType',
                                                            ActivityTypes.STRATEGIC,
                                                          )
                                                        }
                                                        control={<Radio size="small" />}
                                                        label={
                                                          <Typography variant="body2">
                                                            {t('defineActivities.strategic')}
                                                          </Typography>
                                                        }
                                                      />
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                      <FormControlLabel
                                                        value={ActivityTypes.PERMANENT}
                                                        checked={
                                                          activityEnEdicion[objetivo.APObjectiveId]
                                                            ?.GroupActivityType ===
                                                          ActivityTypes.PERMANENT
                                                        }
                                                        onChange={() =>
                                                          handleactivityChange(
                                                            objetivo.APObjectiveId,
                                                            'GroupActivityType',
                                                            ActivityTypes.PERMANENT,
                                                          )
                                                        }
                                                        control={<Radio size="small" />}
                                                        label={
                                                          <Typography variant="body2">
                                                            {t('defineActivities.permanent')}
                                                          </Typography>
                                                        }
                                                      />
                                                    </Grid>
                                                  </Grid>
                                                </Box>

                                                <Box
                                                  sx={{
                                                    border: '1px solid #A5B2BB',
                                                    borderRadius: 1,
                                                    bgcolor: '#FAFCFC',
                                                    px: 2,
                                                    py: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 500 }}
                                                  >
                                                    {t('defineActivities.applyRequirement', {
                                                      enterpriseName,
                                                    })}
                                                  </Typography>
                                                  <Switch
                                                    checked={
                                                      activityEnEdicion[objetivo.APObjectiveId]
                                                        ?.ApplyRequirement || false
                                                    }
                                                    onChange={(e) =>
                                                      handleactivityChange(
                                                        objetivo.APObjectiveId,
                                                        'ApplyRequirement',
                                                        e.target.checked,
                                                      )
                                                    }
                                                    sx={{
                                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#00BCE4',
                                                      },
                                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                                        {
                                                          bgcolor: '#00BCE4',
                                                        },
                                                    }}
                                                  />
                                                </Box>
                                              </Grid>

                                              <Grid size={{ xs: 12, md: 6 }}>
                                                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                                  <Grid size={{ xs: 12, md: 6 }}>
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="subtitle2"
                                                        sx={{ mb: 0.5, fontWeight: 600 }}
                                                      >
                                                        {t('defineActivities.from')}
                                                      </Typography>
                                                      <LocalizationProvider
                                                        dateAdapter={AdapterDateFns}
                                                        adapterLocale={adapterLocale}
                                                      >
                                                        <DatePicker
                                                          value={
                                                            activityEnEdicion[
                                                              objetivo.APObjectiveId
                                                            ]?.StartDate
                                                              ? new Date(
                                                                  activityEnEdicion[
                                                                    objetivo.APObjectiveId
                                                                  ]!.StartDate,
                                                                )
                                                              : null
                                                          }
                                                          onChange={(date) =>
                                                            handleactivityChange(
                                                              objetivo.APObjectiveId,
                                                              'StartDate',
                                                              date ? date.toISOString() : '',
                                                            )
                                                          }
                                                          minDate={yearBoundaries.start}
                                                          maxDate={yearBoundaries.end}
                                                          format="dd/MM/yyyy"
                                                          slotProps={{
                                                            textField: {
                                                              size: 'small',
                                                              required: true,
                                                              fullWidth: true,
                                                              placeholder: t('common.dateFormat'),
                                                              sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                  bgcolor: 'white',
                                                                  '& fieldset': {
                                                                    borderColor: '#A5B2BB',
                                                                  },
                                                                },
                                                              },
                                                            },
                                                          }}
                                                        />
                                                      </LocalizationProvider>
                                                    </Box>
                                                  </Grid>
                                                  <Grid size={{ xs: 12, md: 6 }}>
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="subtitle2"
                                                        sx={{ mb: 0.5, fontWeight: 600 }}
                                                      >
                                                        {t('defineActivities.to')}
                                                      </Typography>
                                                      <LocalizationProvider
                                                        dateAdapter={AdapterDateFns}
                                                        adapterLocale={adapterLocale}
                                                      >
                                                        <DatePicker
                                                          required
                                                          value={
                                                            activityEnEdicion[
                                                              objetivo.APObjectiveId
                                                            ]?.EndDate
                                                              ? new Date(
                                                                  activityEnEdicion[
                                                                    objetivo.APObjectiveId
                                                                  ]!.EndDate,
                                                                )
                                                              : null
                                                          }
                                                          onChange={(date) =>
                                                            handleactivityChange(
                                                              objetivo.APObjectiveId,
                                                              'EndDate',
                                                              date ? date.toISOString() : '',
                                                            )
                                                          }
                                                          minDate={yearBoundaries.start}
                                                          maxDate={yearBoundaries.end}
                                                          format="dd/MM/yyyy"
                                                          slotProps={{
                                                            textField: {
                                                              size: 'small',
                                                              fullWidth: true,
                                                              required: true,
                                                              placeholder: t('common.dateFormat'),
                                                              sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                  bgcolor: 'white',
                                                                  '& fieldset': {
                                                                    borderColor: '#A5B2BB',
                                                                  },
                                                                },
                                                              },
                                                            },
                                                          }}
                                                        />
                                                      </LocalizationProvider>
                                                    </Box>
                                                  </Grid>
                                                </Grid>

                                                <Typography
                                                  variant="subtitle2"
                                                  sx={{ mb: 0.5, fontWeight: 600, mt: 5.5 }}
                                                >
                                                  {t('defineActivities.description')}
                                                </Typography>
                                                <TextField
                                                  fullWidth
                                                  placeholder={t('defineActivities.enterText')}
                                                  multiline
                                                  rows={3}
                                                  value={
                                                    activityEnEdicion[objetivo.APObjectiveId]
                                                      ?.Description || ''
                                                  }
                                                  onChange={(e) =>
                                                    handleactivityChange(
                                                      objetivo.APObjectiveId,
                                                      'Description',
                                                      e.target.value,
                                                    )
                                                  }
                                                  size="small"
                                                  sx={{
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': {
                                                      bgcolor: 'white',
                                                      '& fieldset': {
                                                        borderColor: '#A5B2BB',
                                                      },
                                                    },
                                                  }}
                                                />
                                                {activityEnEdicion[objetivo.APObjectiveId]
                                                  ?.ApplyRequirement && (
                                                  <>
                                                    <Typography
                                                      variant="subtitle2"
                                                      sx={{ mb: 0.5, fontWeight: 600 }}
                                                    >
                                                      {t('defineActivities.selectRequirement')}
                                                    </Typography>
                                                    <FormControl
                                                      fullWidth
                                                      size="small"
                                                      disabled={
                                                        !activityEnEdicion[objetivo.APObjectiveId]
                                                          ?.ApplyRequirement
                                                      }
                                                    >
                                                      <Select
                                                        value={
                                                          activityEnEdicion[objetivo.APObjectiveId]
                                                            ?.APRequirementId || ''
                                                        }
                                                        onChange={(e) =>
                                                          handleactivityChange(
                                                            objetivo.APObjectiveId,
                                                            'APRequirementId',
                                                            e.target.value,
                                                          )
                                                        }
                                                        displayEmpty
                                                        renderValue={(value) => {
                                                          if (!value) return t('common.select');

                                                          const currentFocusRequirements =
                                                            getRequirementsForCurrentFocus(
                                                              focus.APFocusId,
                                                            );
                                                          for (const reqType of currentFocusRequirements) {
                                                            const req = reqType.requirements.find(
                                                              (r) =>
                                                                r.APRequirementId === Number(value),
                                                            );
                                                            if (req) return req.Name;
                                                          }
                                                          return value;
                                                        }}
                                                        sx={{
                                                          bgcolor: 'white',
                                                          '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#A5B2BB',
                                                          },
                                                        }}
                                                      >
                                                        {getRequirementsForCurrentFocus(
                                                          focus.APFocusId,
                                                        ).length === 0 ? (
                                                          <MenuItem disabled value="">
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontStyle: 'italic',
                                                                color: '#999',
                                                              }}
                                                            >
                                                              {t('defineActivities.noRequirements')}
                                                            </Typography>
                                                          </MenuItem>
                                                        ) : (
                                                          getRequirementsForCurrentFocus(
                                                            focus.APFocusId,
                                                          )
                                                            .map((reqTypeGroup, groupIdx) => [
                                                              <MenuItem
                                                                key={`header-${groupIdx}`}
                                                                disabled
                                                                value=""
                                                              >
                                                                <Typography
                                                                  variant="body2"
                                                                  sx={{
                                                                    fontWeight: 600,
                                                                    mt: groupIdx > 0 ? 1 : 0,
                                                                  }}
                                                                >
                                                                  {
                                                                    reqTypeGroup.requirementType
                                                                      .Name
                                                                  }
                                                                </Typography>
                                                              </MenuItem>,
                                                              ...reqTypeGroup.requirements.map(
                                                                (req, reqIdx) => (
                                                                  <MenuItem
                                                                    key={`req-${groupIdx}-${reqIdx}`}
                                                                    value={req.APRequirementId}
                                                                    sx={{ pl: 4 }}
                                                                  >
                                                                    {req.Name}
                                                                  </MenuItem>
                                                                ),
                                                              ),
                                                            ])
                                                            .flat()
                                                        )}
                                                      </Select>
                                                    </FormControl>
                                                  </>
                                                )}
                                              </Grid>

                                              <Grid
                                                size={12}
                                                sx={{
                                                  display: 'flex',
                                                  justifyContent: 'flex-end',
                                                  mt: 1,
                                                }}
                                              >
                                                <Tooltip
                                                  title={getValidationMessage(
                                                    objetivo.APObjectiveId,
                                                  )}
                                                  placement="top"
                                                >
                                                  <span>
                                                    {' '}
                                                    {/* Necesario para Tooltip con botón deshabilitado */}
                                                    <Button
                                                      variant="contained"
                                                      disabled={
                                                        !isFormValid(objetivo.APObjectiveId) ||
                                                        isAnyLoading
                                                      }
                                                      startIcon={
                                                        editandoactivity[objetivo.APObjectiveId] ? (
                                                          <Check />
                                                        ) : (
                                                          <Add />
                                                        )
                                                      }
                                                      onClick={() =>
                                                        handleAgregaractivity(
                                                          objetivo.APObjectiveId,
                                                        )
                                                      }
                                                      sx={{
                                                        bgcolor: editandoactivity[
                                                          objetivo.APObjectiveId
                                                        ]
                                                          ? '#4caf50'
                                                          : '#00BCE4',
                                                        color: 'white',
                                                        px: 4,
                                                        py: 1,
                                                        borderRadius: 20,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        '&:hover': {
                                                          bgcolor: editandoactivity[
                                                            objetivo.APObjectiveId
                                                          ]
                                                            ? '#45a049'
                                                            : '#00acc1',
                                                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                                        },
                                                        '&:disabled': {
                                                          bgcolor: '#ccc',
                                                          color: '#888',
                                                          boxShadow: 'none',
                                                        },
                                                      }}
                                                    >
                                                      {editandoactivity[objetivo.APObjectiveId]
                                                        ? t('defineActivities.saveActivity')
                                                        : t('defineActivities.addActivity')}
                                                    </Button>
                                                  </span>
                                                </Tooltip>
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        </>
                                      )}

                                      {(activitiesPorObjetivo[objetivo.APObjectiveId]?.length ??
                                        0) > 0 && (
                                        <Box sx={{ mt: 4 }}>
                                          <Paper
                                            elevation={0}
                                            sx={{
                                              borderRadius: 1,
                                              p: 1,
                                              overflow: 'hidden',
                                              mb: 2,
                                              border: '1px solid #A5B2BB',
                                            }}
                                          >
                                            <TableContainer>
                                              <Table size="medium">
                                                <TableHead>
                                                  <TableRow sx={{ bgcolor: 'transparent' }}>
                                                    {[
                                                      t('table.activityName'),
                                                      t('table.activityDescription'),
                                                      t('table.activityTerm'),
                                                      t('table.activityType'),
                                                      t('table.activityRequirement'),
                                                      t('table.activityAction'),
                                                    ].map((label, idx) => (
                                                      <TableCell
                                                        key={label}
                                                        align={idx === 5 ? 'center' : 'left'}
                                                        sx={{
                                                          p: 0.25,
                                                          borderBottom: 'none',
                                                          bgcolor: 'transparent',
                                                        }}
                                                      >
                                                        <Box
                                                          sx={{
                                                            bgcolor: '#DEEAEB',
                                                            border: `1px solid #d5e2e7`,
                                                            px: 1,
                                                            py: 1,
                                                            height: 48,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderTopLeftRadius: 8,
                                                            borderTopRightRadius: 8,
                                                            boxShadow:
                                                              'inset 0 1px 0 rgba(255,255,255,0.85), 0 1px 2px rgba(0,0,0,0.06)',
                                                          }}
                                                        >
                                                          <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                              fontWeight: 600,
                                                              color: '#333',
                                                              lineHeight: 1.2,
                                                              whiteSpace: 'nowrap',
                                                            }}
                                                          >
                                                            {label}
                                                          </Typography>
                                                        </Box>
                                                      </TableCell>
                                                    ))}
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {(
                                                    activitiesPorObjetivo[objetivo.APObjectiveId] ??
                                                    []
                                                  ).map((activity, index, arr) => {
                                                    const rowBg =
                                                      index % 2 === 0 ? '#EFF5F5' : '#FAFCFC';
                                                    const isLastRow = index === arr.length - 1;
                                                    const innerCommon = {
                                                      border: `1px solid #e1eaee`,
                                                      px: 1,
                                                      py: 1,
                                                      bgcolor: rowBg,
                                                      boxShadow:
                                                        'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 1px rgba(0,0,0,0.04)',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      height: 48,
                                                      ...(isLastRow && {
                                                        borderBottomLeftRadius: 8,
                                                        borderBottomRightRadius: 8,
                                                      }),
                                                    } as const;

                                                    const requirementName =
                                                      activity.ApplyRequirement
                                                        ? getRequirementNameById(
                                                            activity.APRequirementId,
                                                            focus.APFocusId,
                                                          )
                                                        : t('defineActivities.noApply');

                                                    return (
                                                      <TableRow
                                                        key={activity.APGroupActivityId}
                                                        sx={{ bgcolor: 'transparent' }}
                                                      >
                                                        <TableCell
                                                          sx={{ p: 0.25, borderBottom: 'none' }}
                                                        >
                                                          <Box sx={innerCommon}>
                                                            <Tooltip
                                                              title={activity.GroupActivityName}
                                                              placement="top"
                                                            >
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontSize: '0.875rem',
                                                                  overflow: 'hidden',
                                                                  textOverflow: 'ellipsis',
                                                                  whiteSpace: 'nowrap',
                                                                  maxWidth: '200px',
                                                                  cursor: 'default',
                                                                }}
                                                              >
                                                                {activity.GroupActivityName.length >
                                                                120
                                                                  ? `${activity.GroupActivityName.substring(0, 120)}...`
                                                                  : activity.GroupActivityName}
                                                              </Typography>
                                                            </Tooltip>
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ p: 0.25, borderBottom: 'none' }}
                                                        >
                                                          <Box sx={innerCommon}>
                                                            <Tooltip
                                                              title={activity.Description}
                                                              placement="top"
                                                            >
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontSize: '0.875rem',
                                                                  overflow: 'hidden',
                                                                  textOverflow: 'ellipsis',
                                                                  whiteSpace: 'nowrap',
                                                                  maxWidth: '200px',
                                                                  cursor: 'default',
                                                                }}
                                                              >
                                                                {activity.Description.length > 120
                                                                  ? `${activity.Description.substring(0, 120)}...`
                                                                  : activity.Description}
                                                              </Typography>
                                                            </Tooltip>
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ p: 0.25, borderBottom: 'none' }}
                                                        >
                                                          <Box sx={innerCommon}>
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontSize: '0.875rem',
                                                              }}
                                                            >
                                                              {activity.StartDate
                                                                ? new Date(
                                                                    activity.StartDate,
                                                                  ).toLocaleDateString()
                                                                : '-'}{' '}
                                                              {t('pdf.separator')}{' '}
                                                              {activity.EndDate
                                                                ? new Date(
                                                                    activity.EndDate,
                                                                  ).toLocaleDateString()
                                                                : '-'}
                                                            </Typography>
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ p: 0.25, borderBottom: 'none' }}
                                                        >
                                                          <Box
                                                            sx={{
                                                              ...innerCommon,
                                                              justifyContent: 'center',
                                                            }}
                                                          >
                                                            <Box
                                                              sx={{
                                                                bgcolor:
                                                                  activity.GroupActivityType ===
                                                                  ActivityTypes.STRATEGIC
                                                                    ? '#D8FFEC'
                                                                    : '#FEFFC2',
                                                                border:
                                                                  activity.GroupActivityType ===
                                                                  ActivityTypes.STRATEGIC
                                                                    ? '1px solid #37F199'
                                                                    : '1px solid #FFE810',
                                                                px: 1.5,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                fontSize: '0.8125rem',
                                                                minHeight: 30,
                                                                fontWeight: 400,
                                                                textAlign: 'center',
                                                                borderRadius: 1,
                                                              }}
                                                            >
                                                              {activity.GroupActivityType ===
                                                              ActivityTypes.STRATEGIC
                                                                ? t('defineActivities.strategic')
                                                                : t('defineActivities.permanent')}
                                                            </Box>
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ p: 0.25, borderBottom: 'none' }}
                                                        >
                                                          <Box sx={innerCommon}>
                                                            <Tooltip
                                                              title={requirementName}
                                                              placement="top"
                                                            >
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontSize: '0.875rem',
                                                                  overflow: 'hidden',
                                                                  textOverflow: 'ellipsis',
                                                                  whiteSpace: 'nowrap',
                                                                  maxWidth: '200px',
                                                                  cursor: 'default',
                                                                }}
                                                              >
                                                                {requirementName.length > 120
                                                                  ? `${requirementName.substring(0, 120)}...`
                                                                  : requirementName}
                                                              </Typography>
                                                            </Tooltip>
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ p: 0.25, borderBottom: 'none' }}
                                                        >
                                                          <Box
                                                            sx={{
                                                              ...innerCommon,
                                                              justifyContent: 'center',
                                                              gap: 0.25,
                                                            }}
                                                          >
                                                            <IconButton
                                                              size="small"
                                                              disabled={readOnlyMode ?? false}
                                                              onClick={() =>
                                                                handleEditarActivity(
                                                                  objetivo.APObjectiveId,
                                                                  activity,
                                                                )
                                                              }
                                                              sx={{
                                                                bgcolor: '#1976d2',
                                                                color: '#fff',
                                                                borderRadius: '8px',
                                                                width: 32,
                                                                height: 32,
                                                                p: 0,
                                                                '&:hover': {
                                                                  bgcolor: '#1565c0',
                                                                },
                                                              }}
                                                            >
                                                              <Edit fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                              size="small"
                                                              disabled={readOnlyMode ?? false}
                                                              onClick={() =>
                                                                handleEliminarActivity(
                                                                  objetivo.APObjectiveId,
                                                                  activity.APGroupActivityId,
                                                                )
                                                              }
                                                              sx={{
                                                                bgcolor: '#f44336',
                                                                color: '#fff',
                                                                borderRadius: '8px',
                                                                width: 32,
                                                                height: 32,
                                                                p: 0,
                                                                '&:hover': {
                                                                  bgcolor: '#d32f2f',
                                                                },
                                                              }}
                                                            >
                                                              <Close fontSize="small" />
                                                            </IconButton>
                                                          </Box>
                                                        </TableCell>
                                                      </TableRow>
                                                    );
                                                  })}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Paper>
                                        </Box>
                                      )}
                                    </Box>
                                  </AccordionDetails>
                                )}
                              </Accordion>
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: '#999', textAlign: 'center', py: 2 }}
                          >
                            {t('defineActivities.noObjectives')}
                          </Typography>
                        )}
                      </AccordionDetails>
                    </>
                  )}
                </Accordion>
              ))}
            </Box>

            {/* Buttons */}
            <Navigation
              onBack={handleBack}
              backTitle={t('common.backToStep1')}
              onSaveDraft={() => handleSaveProgress()}
              saveDraftTitle={t('common.saveProgress')}
              onExit={() => handleExit()}
              exitTitle={t('common.exit')}
              readOnlyMode={readOnlyMode}
              isAnyLoading={isAnyLoading}
              onNext={() => handleContinue()}
              disableNext={disableNext}
              disableSaveDraft={false}
              nextTitle={readOnlyMode ? t('common.continue') : t('common.continueWithApproval')}
              {...navigationDisableProps}
            />
          </Box>
        </Paper>
      </Box>
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 0,
            pt: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('defineActivities.confirmCloseUp')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Warning
              sx={{
                fontSize: 64,
                color: '#ffa726',
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#333',
              }}
            >
              {t('checklist.confirmCloseMessage')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                lineHeight: 1.6,
                maxWidth: '80%',
              }}
            >
              {t('checklist.confirmSubmissionWarning')}
            </Typography>
            {/* Alignment summary (in-memory) */}
            <Box
              sx={{
                mt: 3,
                width: '100%',
                bgcolor: '#F4F8F8',
                border: '1px solid #A5B2BB',
                borderRadius: 1,
                p: 2,
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                {t('approvalDefinitions.totalActivitiesAdherencePctUltramar', { enterpriseName })}:{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    bgcolor: '#79FCBD',
                    px: 1,
                    borderRadius: 1,
                  }}
                >
                  {formatPercentage(globalAdherence, 1)}%
                </Box>
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
                {focuses.map((f, idx) => {
                  const data = adherenceByFocus[f.APFocusId];
                  const pct = data?.AdherencePct ?? 0;
                  const hasRequired = (data?.TotalRequired ?? 0) > 0;
                  return (
                    <Box
                      key={f.APFocusId}
                      component="li"
                      sx={{ mb: 0.5, pr: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#333' }}>
                        {idx + 1}. {f.Name}:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: hasRequired ? '#000' : '#777',
                        }}
                        title={
                          hasRequired
                            ? `${data?.SatisfiedRequired || 0}/${data?.TotalRequired || 0}`
                            : t('approvalDefinitions.noMandatoryRequirements')
                        }
                      >
                        {hasRequired ? `${formatPercentage(pct, 1)}%` : 'N/A'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 3,
            px: 4,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            disabled={isAnyLoading}
            onClick={() => setShowConfirmModal(false)}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('defineActivities.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isAnyLoading}
            onClick={handleConfirmSave}
            sx={{
              bgcolor: '#00BCE4',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 150,
              '&:hover': { bgcolor: '#00acc1' },
            }}
          >
            {t('defineActivities.finishAndSent')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Definiractivities;
